// University of Arkansas: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - The U of A's Common Data Set 2025-2026 (cds25-26v4.pdf, the version
//    the Office of Strategic Analytics & Insights posts, created September
//    11, 2026; read from the PDF and its rendered pages): C1 applicants,
//    admits and enrollees for Fall 2025 with the residency table (4 applicants
//    of unknown residency, none admitted, are left out of the rows); C8 test
//    policy for Fall 2027; C21/C22 plans; G1/G5 2026-2027 costs. The SOURCES
//    url is the OSAI page that links the file.
//  - U of A Admissions, New Freshman page: dates (fall application opens
//    July 15, November 1 priority deadline, August 1 final deadline), the
//    test-optional rule for fall 2027 (scores due May 1, 2027), the
//    architecture and engineering major notes. It publishes no decision
//    dates. No change for fall 2028 entry was announced there.
//  - College Scorecard, school id 106397 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Quirks: the CDS calls the November 1 date early action (notification
// December 15); the admissions page calls it the priority application
// deadline. The CDS gives August 1 as the latest date for test scores; the
// admissions page says May 1, 2027. Both are stated as each source has them.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'University of Arkansas Common Data Set 2025-2026',
        url: 'https://osai.uark.edu/datasets/cds/index.php',
        read: '2026-10-01',
    },
    newFreshman: {
        label: 'University of Arkansas Admissions, New Freshman',
        url: 'https://admissions.uark.edu/apply/new-freshman.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?106397-University-of-Arkansas',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-arkansas',
    kbId: 'university_of_arkansas',
    name: 'University of Arkansas',
    shortName: 'Arkansas',
    aliases: ['U of A', 'UARK'],
    location: 'Fayetteville, AR',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (U of A brand colors: Razorback Red,
    // white); decoration only.
    branding: {
        primary: '#9D2235',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_arkansas_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 32928,
        admitted: 24326,
        enrolled: 6956,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 8988, admitted: 7116 },
            { group: 'Out-of-state', applied: 22358, admitted: 17028 },
            { group: 'International', applied: 1578, admitted: 182 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'The U of A\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'The U of A has no early decision plan. The Common Data Set lists a nonbinding early action plan with a November 1 closing date and December 15 notification, but does not report how many applied or were admitted under it, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to the university, and most students have time to choose a major. Two areas want an early choice:',
        rows: [
            { school: 'Architecture', requirement: 'Declare the major at the time of admission.' },
            { school: 'Engineering', requirement: 'Declare the major during the First-Year Engineering program.' },
        ],
        source: 'newFreshman',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority deadline', value: 'November 1' },
        rows: [
            { plan: 'Priority application deadline', deadline: 'November 1', decision: null, binding: false },
            { plan: 'Final application deadline', deadline: 'August 1', decision: null, binding: false },
        ],
        note: 'The fall application opens July 15. Applying by November 1 gives early housing and orientation options, and applicants who send everything by then get the greatest consideration. The priority scholarship deadline is November 15 and the final scholarship deadline is February 1. Deferred applicants hear on or about February 1. The U of A does not publish other decision dates.',
        source: 'newFreshman',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Test-optional if your high school GPA is 3.20 or higher on a 4.00 scale. Below 3.20, send ACT, SAT, Next Generation Accuplacer or Classic Learning Test scores when you apply; home-schooled applicants and those from schools with narrative transcripts need scores too. Every admitted student must have one of those scores on file by May 1, 2027, for placement and enrollment, and most merit scholarships need ACT or SAT scores by February 1, 2027.',
        source: 'newFreshman',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for some applicants for fall 2027, with scores due by August 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'newFreshman',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [8336, 30604] },
            { item: 'Required fees', amounts: [2580, 2580] },
            { item: 'Housing (on campus)', amounts: [10144, 10144] },
            { item: 'Food (on-campus meal plan)', amounts: [5550, 5550] },
            { item: 'Books and supplies', amounts: [1270, 1270] },
            { item: 'Transportation', amounts: [2656, 2656] },
            { item: 'Other expenses', amounts: [3452, 3452] },
        ],
        totals: [33988, 56256],
        note: 'For a full-time undergraduate living on campus, fall and spring. The Common Data Set notes that tuition and fees vary by program.',
        source: 'cds',
        where: 'sections G1, G4 and G5',
        netPrice: {
            average: 17571,
            byIncome: [
                { income: '$0 to $30,000', amount: 12718 },
                { income: '$30,001 to $48,000', amount: 12559 },
                { income: '$48,001 to $75,000', amount: 14381 },
                { income: '$75,001 to $110,000', amount: 19256 },
                { income: 'Over $110,000', amount: 23211 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from the U of A\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
